import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';
import { toast } from 'sonner';
import useSWR from 'swr';
import { BackButton } from '@/components/ui/back-button';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Toggle } from '@/components/ui/toggle';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { SelectionActionBar } from '@/components/ui/selection-action-bar';
import { InformationCircleIcon, LinkIcon } from '@heroicons/react/24/outline';
import { MagnifyingGlassIcon, UserIcon, EnvelopeIcon } from '@heroicons/react/24/solid';
import { agentService, type Agent } from '@/lib/services/agents';
import { useEmailAccounts } from '@/lib/hooks/useEmailAccount';
import { renderMergeTags, splitName, type MergeTagValues } from '@/lib/merge-tags';
import { peopleService, type Person } from '@/lib/services/people';
import { contactsService } from '@/lib/services/contacts';
import { AddToCampaignModal, type ProspectForCampaign } from '@/components/campaigns';
import { GmailPreviewModal } from '@/components/ui/gmail-preview-modal';
import { DialogCloseButton } from '@/components/ui/dialog-close-button';
import { EmailEditor } from '@/components/ui/email-editor';
import {
  DataTable,
  DataTablePagination,
  useDataTableColumns,
  useDataTableSelection,
  type ColumnDefinition,
  type RowAction,
} from '@/components/data-table';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DataTableEditableCell } from '@/components/data-table/DataTableEditableCell';
import { Textarea } from '@/components/ui/textarea';
import { Phase1BusinessAnalysis } from '@/components/agent-wizard/Phase1BusinessAnalysis';
import { ProposalCard } from '@/components/agent-wizard/ProposalCard';
import type { Phase1Proposal } from '@/lib/services/agent-wizard';

const fetcher = (url: string) => fetch(url, { credentials: 'include' }).then((res) => res.json());

function LinkedInIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"></path>
    </svg>
  );
}

function toTitleCasePeople(text: string | null | undefined): string {
  if (!text) return '';
  return text.toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

const PEOPLE_STORAGE_KEY = 'agentPeople_visibleColumns';

const peopleColumns: ColumnDefinition<Person>[] = [
  { id: 'name', label: 'Name', width: '280px', required: true, sticky: true, skeletonWidth: 'w-32' },
  { id: 'role', label: 'Role', width: '240px', skeletonWidth: 'w-24' },
  { id: 'seniority', label: 'Seniority', width: '150px', skeletonWidth: 'w-24' },
  { id: 'company', label: 'Company', width: '165px', skeletonWidth: 'w-40' },
  { id: 'industry', label: 'Industry', width: '120px', skeletonWidth: 'w-32' },
  { id: 'companyKeywords', label: 'Company Keywords', width: '240px', skeletonWidth: 'w-32' },
  { id: 'companyWebsite', label: 'Company Website', width: '180px', skeletonWidth: 'w-36' },
  { id: 'companySize', label: 'Company Size', width: '130px', skeletonWidth: 'w-16' },
  { id: 'companiesHouse', label: 'Companies House', width: '160px', skeletonWidth: 'w-20' },
  { id: 'approve', label: 'Approve', width: '140px', skeletonWidth: 'w-20' },
  { id: 'location', label: 'Location', width: '192px', skeletonWidth: 'w-28' },
];

function PeopleTab({ agentId, scrapeIsActive, onApproved }: { agentId: string; scrapeJobId: string | null; scrapeIsActive: boolean; onApproved: () => void }) {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [people, setPeople] = useState<Person[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [addToCampaignOpen, setAddToCampaignOpen] = useState(false);
  const [prospectsForCampaign, setProspectsForCampaign] = useState<ProspectForCampaign[]>([]);
  const pageSize = 50;

  const { selectedIds, toggle, selectAll, clearSelection, selectedCount } = useDataTableSelection({ data: people });
  const { visibleColumns, saveVisibleColumns } = useDataTableColumns({ columns: peopleColumns, storageKey: PEOPLE_STORAGE_KEY });

  const fetchPeople = useCallback(async (page: number = 1) => {
    if (!agentId) return;
    setLoading(true);
    try {
      const response = await peopleService.searchPeople({ externalAgentId: agentId, limit: pageSize, page } as any);
      setPeople(response.persons);
      setTotal(response.total);
      setCurrentPage(page);
    } catch (error) {
      console.error('Failed to fetch people:', error);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => { fetchPeople(1); }, [fetchPeople]);

  // Auto-refresh while scraper is active
  useEffect(() => {
    if (!scrapeIsActive) return;
    const interval = setInterval(() => fetchPeople(currentPage), 5000);
    return () => clearInterval(interval);
  }, [scrapeIsActive, fetchPeople, currentPage]);

  const totalPages = Math.ceil(total / pageSize);

  const handlePushToCampaign = (person: Person) => {
    setProspectsForCampaign([{ id: person.id, enrichmentStatus: person.enrichment_status, enrichedEmail: person.enriched_email }]);
    setAddToCampaignOpen(true);
  };

  const handlePushToContacts = async (person: Person) => {
    const loadingToast = toast.loading('Finding email...');
    try {
      if (person.enrichment_status !== 'enriched') {
        const enrichResult = await peopleService.enrichProspect(person.id);
        if (!enrichResult.success) {
          toast.dismiss(loadingToast);
          toast.error('Could not find email for this person.');
          return;
        }
      }
      toast.loading('Adding to contacts...', { id: loadingToast });
      const result = await contactsService.addProspectsToContacts([person.id]);
      toast.dismiss(loadingToast);
      if (result.success) toast.success('Added to contacts');
      else toast.error(result.message || 'Failed to add to contacts');
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error instanceof Error ? error.message : 'Failed to add to contacts');
    }
  };

  const handleApprovePerson = async (person: Person) => {
    try {
      const res = await fetch(`/api/agents/${agentId}/approve-person`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ person }),
      });
      if (res.ok) {
        toast.success(`${toTitleCasePeople(person.first_name)} ${toTitleCasePeople(person.last_name)} approved as lead`);
        onApproved();
      } else {
        toast.error('Failed to approve');
      }
    } catch {
      toast.error('Failed to approve');
    }
  };

  const handleBulkAddToCampaign = () => {
    const selected = people.filter(p => selectedIds.has(p.id)).map(p => ({ id: p.id, enrichmentStatus: p.enrichment_status, enrichedEmail: p.enriched_email }));
    if (selected.length === 0) { toast.error('No prospects selected.'); return; }
    setProspectsForCampaign(selected);
    setAddToCampaignOpen(true);
  };

  const rowActions: RowAction<Person>[] = [
    { label: 'Push to campaign', onClick: handlePushToCampaign },
    { label: 'Push to contacts', onClick: handlePushToContacts },
  ];

  const columnsWithCells: ColumnDefinition<Person>[] = peopleColumns.map(col => {
    if (col.id === 'name') {
      return { ...col, cell: (person: Person) => (
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Checkbox checked={selectedIds.has(person.id)} onCheckedChange={() => toggle(person.id)} />
            {person.profile_image_url
              && !person.profile_image_url.startsWith('data:image/gif')
              && !person.profile_image_url.includes('static.licdn.com') ? (
              <Avatar className="flex-shrink-0 outline-none">
                <AvatarImage src={person.profile_image_url} alt={`${person.first_name} ${person.last_name}`} />
                <AvatarFallback className="bg-surface-avatar"><UserIcon className="h-4 w-4 text-muted-foreground" /></AvatarFallback>
              </Avatar>
            ) : (
              <div className="w-8 h-8 rounded-full bg-surface-avatar flex items-center justify-center flex-shrink-0">
                <UserIcon className="h-4 w-4 text-muted-foreground" />
              </div>
            )}
            <div className="font-normal text-content-heading text-sm whitespace-nowrap overflow-hidden text-ellipsis" title={`${toTitleCasePeople(person.first_name)} ${toTitleCasePeople(person.last_name)}`}>
              {toTitleCasePeople(person.first_name)} {toTitleCasePeople(person.last_name)}
            </div>
          </div>
          {person.linkedin_url && (
            <TooltipProvider><Tooltip delayDuration={300}><TooltipTrigger asChild>
              <a href={person.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 text-primary hover:text-primary/80 transition-colors" onClick={e => e.stopPropagation()}>
                <LinkedInIcon className="h-4 w-4" />
              </a>
            </TooltipTrigger><TooltipContent><p className="text-xs">{person.linkedin_url}</p></TooltipContent></Tooltip></TooltipProvider>
          )}
        </div>
      )};
    }
    if (col.id === 'role') return { ...col, accessor: (p: Person) => p.title || '' };
    if (col.id === 'company') return { ...col, accessor: (p: Person) => toTitleCasePeople(p.company?.company_name?.replace(/\s+\b(ltd|limited|plc|llp|lp|cic|inc)\.?\s*$/i, '')) };
    if (col.id === 'companySize') return { ...col, cell: (p: Person) => {
      const range = p.company?.employee_count_range;
      return range ? <span className="text-sm">{range} employees</span> : <span className="text-content-subtle text-sm">-</span>;
    }};
    if (col.id === 'industry') return { ...col, accessor: (p: Person) => toTitleCasePeople(p.company?.company_category) };
    if (col.id === 'companyWebsite') return { ...col, cell: (p: Person) => p.company?.website ? (
      <a href={p.company.website.startsWith('http') ? p.company.website : `https://${p.company.website}`} target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-foreground/80 text-sm flex items-center gap-1.5 min-w-0" onClick={e => e.stopPropagation()}>
        <LinkIcon className="h-3.5 w-3.5 flex-shrink-0" /><span className="truncate">{p.company.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}</span>
      </a>
    ) : <span className="text-content-subtle text-sm">-</span> };
    if (col.id === 'companyKeywords') return { ...col, cell: (p: Person) => {
      const kws = p.sic_keywords;
      if (!kws || kws.length === 0) return <span className="text-content-subtle text-sm">-</span>;
      return (<div className="relative overflow-hidden -mr-4"><div className="flex gap-1 flex-nowrap">
        {kws.map((kw, i) => <span key={i} className="inline-block bg-surface-subtle text-content-body-strong text-xs px-2 py-0.5 rounded-full whitespace-nowrap">{kw}</span>)}
      </div><div className="absolute top-0 right-0 bottom-0 w-8 bg-gradient-to-l from-card to-card/0 pointer-events-none" /></div>);
    }};
    if (col.id === 'companiesHouse') return { ...col, cell: (p: Person) => {
      if (p.companies_house_url) return <a href={p.companies_house_url} target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-foreground/80 text-sm flex items-center gap-1" onClick={e => e.stopPropagation()}><LinkIcon className="h-3 w-3" /></a>;
      const name = p.company?.company_name;
      if (name) return <a href={`https://find-and-update.company-information.service.gov.uk/search?q=${encodeURIComponent(name)}`} target="_blank" rel="noopener noreferrer" className="text-content-subtle hover:text-foreground/80 text-sm flex items-center gap-1" onClick={e => e.stopPropagation()}><LinkIcon className="h-3 w-3" /></a>;
      return <span className="text-content-subtle text-sm">-</span>;
    }};
    if (col.id === 'approve') return { ...col, cell: (p: Person) => (
      <Button variant="secondary" size="xs" className="text-sm whitespace-nowrap shadow-xl z-10 bg-card transform-gpu" onClick={() => handleApprovePerson(p)}>Approve</Button>
    )};
    if (col.id === 'seniority') return { ...col, accessor: (p: Person) => toTitleCasePeople(p.seniority_level) };
    if (col.id === 'location') return { ...col, accessor: (p: Person) => toTitleCasePeople(p.location) };
    return col;
  });

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 12rem)' }}>
      <div className="bg-card flex-1 min-h-0 rounded-card overflow-hidden relative flex flex-col" ref={tableContainerRef} style={{ contain: 'layout paint' }}>
        <DataTable<Person>
          data={people}
          columns={columnsWithCells}
          isLoading={loading}
          resizable
          resizeConfig={{ storageKey: 'agentPeople_columnWidths' }}
          selection={{ selectedIds, onToggle: toggle, onSelectAll: selectAll, onClearSelection: clearSelection }}
          rowActions={rowActions}
          columnVisibility={{ visibleColumns, onSave: saveVisibleColumns, storageKey: PEOPLE_STORAGE_KEY }}
          emptyState={
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <UserIcon className="h-10 w-10 text-muted-foreground/30 mb-3" />
              <p className="text-sm">No people found yet</p>
              <p className="text-xs text-muted-foreground/60 mt-1">The scraper is finding people matching your ICP</p>
            </div>
          }
        />
      </div>

      {total >= 0 && (
        <DataTablePagination
          config={{ variant: 'animated', currentPage, totalPages, onPageChange: (page) => fetchPeople(page) }}
          totalLabel="people found"
          total={total}
        />
      )}

      <SelectionActionBar selectedCount={selectedCount} isVisible={selectedCount > 0} onClearSelection={clearSelection} itemName={{ singular: 'person', plural: 'people' }} tableContainerRef={tableContainerRef} selectedRowIds={Array.from(selectedIds) as string[]}>
        <Button variant="primary" size="xs" onClick={handleBulkAddToCampaign}>Add to Campaign</Button>
      </SelectionActionBar>

      <AddToCampaignModal open={addToCampaignOpen} onOpenChange={(open) => { setAddToCampaignOpen(open); if (!open) setProspectsForCampaign([]); }} prospects={prospectsForCampaign} onSuccess={() => { clearSelection(); setProspectsForCampaign([]); fetchPeople(currentPage); }} />
    </div>
  );
}

interface AgentLead {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  title: string | null;
  company: string | null;
  companyWebsite: string | null;
  linkedinUrl: string | null;
  location: string | null;
  status: string;
  prospectExternalId: string | null;
  enrichmentAttempted: boolean;
  createdAt: string;
}

const LEADS_STORAGE_KEY = 'agentLeads_visibleColumns';

const leadColumns: ColumnDefinition<AgentLead>[] = [
  { id: 'select', label: '', width: '144px', required: true, resizable: false, skeletonWidth: 'w-8' },
  { id: 'firstName', label: 'First Name', width: '200px', minWidth: 120, required: true, skeletonWidth: 'w-28' },
  { id: 'lastName', label: 'Last Name', width: '200px', minWidth: 120, required: true, skeletonWidth: 'w-28' },
  { id: 'email', label: 'Email', width: '240px', minWidth: 150, skeletonWidth: 'w-40' },
  { id: 'role', label: 'Role', width: '240px', minWidth: 100, skeletonWidth: 'w-28' },
  { id: 'company', label: 'Company', width: '200px', minWidth: 120, skeletonWidth: 'w-32' },
  { id: 'companyWebsite', label: 'Company Website', width: '200px', minWidth: 150, skeletonWidth: 'w-36' },
  { id: 'location', label: 'Location', width: '200px', minWidth: 100, skeletonWidth: 'w-24' },
];

// --- Campaigns Tab ---
type AgentCampaign = {
  id: string;
  name: string;
  status: string;
  sent: number;
  replied: number;
  bounced: number;
  pendingApproval: number;
  createdAt: string;
};

function CampaignsTab({ agentId }: { agentId: string }) {
  const navigate = useNavigate();
  const { data: agentCampaigns, isLoading } = useSWR<AgentCampaign[]>(
    `/api/agents/${agentId}/campaigns`,
    fetcher
  );

  if (isLoading) return <Skeleton className="h-40 w-full rounded-xl" />;

  const campaignsList = Array.isArray(agentCampaigns) ? agentCampaigns : [];

  if (campaignsList.length === 0) {
    return (
      <div className="text-center py-16 text-muted-foreground">
        <EnvelopeIcon className="h-10 w-10 mx-auto mb-3 opacity-40" />
        <p>No campaigns linked to this agent yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {campaignsList.map(campaign => (
        <div
          key={campaign.id}
          className="flex items-center justify-between p-4 rounded-3xl border bg-card hover:bg-accent cursor-pointer transition-colors"
          onClick={() => navigate(`/dashboard/campaigns/${campaign.id}/edit?tab=sequence&from=/dashboard/agents/${agentId}`)}
        >
          <div className="flex items-center gap-4">
            <div>
              <p className="font-medium">{campaign.name}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Created {new Date(campaign.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-center">
              <p className="text-sm font-medium">{campaign.pendingApproval || 0}</p>
              <p className="text-xs text-muted-foreground">Pending</p>
            </div>
            <div className="text-center">
              <p className="text-sm font-medium">{campaign.sent || 0}</p>
              <p className="text-xs text-muted-foreground">Sent</p>
            </div>
            <div className="text-center">
              <p className="text-sm font-medium">{campaign.replied || 0}</p>
              <p className="text-xs text-muted-foreground">Replied</p>
            </div>
            <div className="text-center">
              <p className="text-sm font-medium">{campaign.bounced || 0}</p>
              <p className="text-xs text-muted-foreground">Bounced</p>
            </div>
            <Badge
              variant="secondary"
              className={campaign.status === 'active' ? 'bg-success-surface-strong text-success-foreground-strong' : campaign.status === 'paused' ? 'bg-warning-surface-strong text-warning-foreground-strong' : ''}
            >
              {campaign.status}
            </Badge>
          </div>
        </div>
      ))}
    </div>
  );
}

// --- Sends Tab ---
type EmailDraft = {
  id: number;
  campaignId: string;
  campaignLeadId: string;
  campaignStepId: string;
  stepIndex: number;
  recipientEmail: string;
  recipientName: string | null;
  subject: string;
  content: string;
  status: string;
  approvedAt: string | null;
  createdAt: string;
  campaignName: string;
  senderEmail: string | null;
  lead: {
    firstName: string | null;
    lastName: string | null;
    company: string | null;
    email: string | null;
    linkedinUrl: string | null;
  } | null;
};

function stripHtml(html: string): string {
  // Strip markdown code fences if present
  let cleaned = html.replace(/^```html?\s*\n?/i, '').replace(/\n?```\s*$/i, '');
  // Break block elements with a space — textContent otherwise runs the last
  // sentence straight into the sign-off in card snippets.
  cleaned = cleaned.replace(/<\/(p|div|li|h[1-6])>/gi, ' ').replace(/<br\s*\/?>/gi, ' ');
  const div = document.createElement('div');
  div.innerHTML = cleaned;
  return (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
}

function SendsTab({ agentId }: { agentId: string }) {
  const navigate = useNavigate();
  const { data: drafts, isLoading, mutate: mutateDrafts } = useSWR<EmailDraft[]>(
    `/api/agents/${agentId}/email-drafts`,
    fetcher
  );
  // Substitute merge tags the way the mailer will at send time, so the
  // approval cards and preview show the email as it will arrive.
  const { emailAccounts } = useEmailAccounts();
  const mergeValuesFor = (draft: EmailDraft): MergeTagValues => {
    const recipient = splitName(draft.recipientName);
    const account = draft.senderEmail
      ? emailAccounts.find(a => a.email.toLowerCase() === draft.senderEmail!.toLowerCase())
      : undefined;
    const sender = splitName(account?.displayName);
    return {
      firstName: draft.lead?.firstName || recipient.first,
      lastName: draft.lead?.lastName || recipient.last,
      company: draft.lead?.company,
      email: draft.lead?.email || draft.recipientEmail,
      linkedinUrl: draft.lead?.linkedinUrl,
      senderFirstName: sender.first,
      senderLastName: sender.last,
    };
  };
  const [previewDraft, setPreviewDraft] = useState<EmailDraft | null>(null);
  const [rewriteDraft, setRewriteDraft] = useState<EmailDraft | null>(null);
  const [rewriteContent, setRewriteContent] = useState('');
  const [rewriteInstructions, setRewriteInstructions] = useState('');
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);

  const openRewrite = (draft: EmailDraft) => {
    setRewriteDraft(draft);
    setRewriteContent(draft.content.replace(/^```html?\s*\n?/i, '').replace(/\n?```\s*$/i, ''));
    setRewriteInstructions('');
  };

  const handleApprove = async (draftId: number) => {
    setActionLoading(draftId);
    try {
      await fetch(`/api/agents/${agentId}/email-drafts/${draftId}/approve`, {
        method: 'PATCH',
        credentials: 'include',
      });
      mutateDrafts();
      toast.success('Email approved');
    } catch {
      toast.error('Failed to approve');
    }
    setActionLoading(null);
  };

  const handleSaveRewrite = async () => {
    if (!rewriteDraft) return;
    setActionLoading(rewriteDraft.id);
    try {
      await fetch(`/api/agents/${agentId}/email-drafts/${rewriteDraft.id}/rewrite`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: rewriteContent }),
      });
      mutateDrafts();
      setRewriteDraft(null);
      toast.success('Email updated');
    } catch {
      toast.error('Failed to save');
    }
    setActionLoading(null);
  };

  const handleRegenerateWithInstructions = async () => {
    if (!rewriteDraft) return;
    setIsRegenerating(true);
    try {
      const res = await fetch(`/api/agents/${agentId}/email-drafts/${rewriteDraft.id}/regenerate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instructions: rewriteInstructions || undefined }),
      });
      const updated = await res.json();
      if (updated.content) {
        setRewriteContent(updated.content);
        setRewriteInstructions('');
        toast.success('Email regenerated');
      }
      mutateDrafts();
    } catch {
      toast.error('Failed to regenerate');
    }
    setIsRegenerating(false);
  };

  const handleCancelSequence = async (draftId: number) => {
    if (!confirm('Cancel the entire email sequence for this lead? They will not receive any more emails.')) return;
    setActionLoading(draftId);
    try {
      await fetch(`/api/agents/${agentId}/email-drafts/${draftId}/cancel-sequence`, {
        method: 'DELETE',
        credentials: 'include',
      });
      mutateDrafts();
      toast.success('Sequence cancelled');
    } catch {
      toast.error('Failed to cancel');
    }
    setActionLoading(null);
  };

  if (isLoading) return <Skeleton className="h-40 w-full rounded-xl" />;

  const draftsList = Array.isArray(drafts) ? drafts : [];

  if (draftsList.length === 0) {
    return (
      <div className="text-center py-16 text-muted-foreground">
        <EnvelopeIcon className="h-10 w-10 mx-auto mb-3 opacity-40" />
        <p>No emails pending approval.</p>
        <p className="text-xs mt-1">Activate a campaign with AI-mode steps to generate drafts.</p>
      </div>
    );
  }

  const pendingDrafts = draftsList.filter(d => d.status === 'pending_approval');
  const otherDrafts = draftsList.filter(d => d.status !== 'pending_approval');

  return (
    <div className="space-y-3">
      {pendingDrafts.length > 0 && (
        <p className="text-sm text-muted-foreground mb-2 ml-4">{pendingDrafts.length} email{pendingDrafts.length !== 1 ? 's' : ''} pending approval</p>
      )}
      {[...pendingDrafts, ...otherDrafts].map(draft => (
        <div
          key={draft.id}
          className={`relative p-6 rounded-3xl border bg-card transition-colors ${
            draft.status === 'approved' ? 'border-success-border bg-success-surface/30' :
            draft.status === 'rejected' ? 'border-danger-border bg-danger-surface/30 opacity-60' :
            ''
          }`}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              {/* Recipient + step badge */}
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-sm truncate">
                  {draft.recipientName || draft.recipientEmail}
                </span>
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">
                  Step {draft.stepIndex + 1}
                </Badge>
                <Badge
                  variant={draft.status === 'rejected' ? 'destructive' : 'secondary'}
                  className={`text-[10px] px-1.5 py-0 shrink-0 ${
                    draft.status === 'approved' ? 'bg-success-surface-strong text-success-foreground-strong' :
                    draft.status === 'pending_approval' ? 'bg-warning-surface-strong text-warning-foreground-strong' : ''
                  }`}
                >
                  {draft.status === 'pending_approval' ? 'Pending' : draft.status === 'approved' ? 'Approved' : 'Rejected'}
                </Badge>
              </div>

              {/* Subject */}
              <p className="text-base font-medium truncate mt-3">{draft.subject}</p>

              {/* Content preview */}
              <div>
                <p className="text-xs text-content-strong truncate mt-1 mb-3">
                  {renderMergeTags(stripHtml(draft.content), mergeValuesFor(draft)).slice(0, 200)}...
                </p>
              </div>

              <div className="flex items-center gap-1.5 flex-nowrap overflow-hidden">
                <span className="text-xs text-content-strong bg-surface-subtle rounded-full px-2.5 py-1 whitespace-nowrap shrink-0">{draft.recipientEmail}</span>
                <span className="text-xs text-content-strong bg-surface-subtle rounded-full px-2.5 py-1 whitespace-nowrap truncate">{draft.campaignName}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPreviewDraft(draft)}
              >
                Show Preview
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => openRewrite(draft)}
              >
                Rewrite
              </Button>
              {draft.status === 'pending_approval' && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleApprove(draft.id)}
                  disabled={actionLoading === draft.id}
                >
                  Approve
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(`/dashboard/campaigns/${draft.campaignId}/edit?tab=sequence&step=${draft.campaignStepId}&from=/dashboard/agents/${agentId}`)}
              >
                View Sequence Step
              </Button>
            </div>
          </div>
          <div className="flex justify-end mt-2 px-1">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-muted-foreground hover:text-danger-text"
              onClick={() => handleCancelSequence(draft.id)}
              disabled={actionLoading === draft.id}
            >
              Cancel Sequence
            </Button>
          </div>
        </div>
      ))}

      <GmailPreviewModal
        isOpen={!!previewDraft}
        onClose={() => setPreviewDraft(null)}
        subject={previewDraft ? renderMergeTags(previewDraft.subject || '', mergeValuesFor(previewDraft)) : ''}
        body={previewDraft ? renderMergeTags(previewDraft.content.replace(/^```html?\s*\n?/i, '').replace(/\n?```\s*$/i, ''), mergeValuesFor(previewDraft)) : ''}
        fromEmail={previewDraft?.senderEmail || 'sender@example.com'}
        toEmail={previewDraft?.recipientEmail || ''}
        toName={previewDraft?.recipientName || undefined}
      />

      {/* Rewrite Modal */}
      {rewriteDraft && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-scrim/50" onClick={() => setRewriteDraft(null)}>
          <div className="relative w-full max-w-3xl bg-popover rounded-panel" onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center justify-between px-8 pt-6 pb-3">
                <div>
                  <h3 className="text-lg font-medium">Rewrite Email</h3>
                  <p className="text-sm text-muted-foreground">
                    To: {rewriteDraft.recipientName || rewriteDraft.recipientEmail}
                  </p>
                </div>
                <DialogCloseButton onClick={() => setRewriteDraft(null)} />
              </div>

              {/* Editor — fixed height, scrolls internally */}
              <div className="px-6 flex flex-col" style={{ height: 'min(calc(80vh - 280px), 420px)' }}>
                <EmailEditor
                  value={rewriteContent}
                  onChange={setRewriteContent}
                  label=""
                  placeholder="Write your email..."
                />
              </div>

              {/* Regenerate with AI */}
              <div className="px-6 pt-3 flex gap-2">
                <Input
                  value={rewriteInstructions}
                  onChange={e => setRewriteInstructions(e.target.value)}
                  placeholder="Optional instructions for AI, e.g. 'make it shorter'"
                  className="flex-1 !mb-0"
                />
                <Button
                  variant="primary"
                  className="h-12 shrink-0"
                  onClick={handleRegenerateWithInstructions}
                  disabled={isRegenerating}
                >
                  {isRegenerating ? 'Regenerating...' : 'Regenerate with AI'}
                </Button>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-2 px-6 py-4">
                <Button variant="secondary" onClick={() => setRewriteDraft(null)}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleSaveRewrite}>
                  Save
                </Button>
              </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AgentEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: agent, isLoading, mutate } = useSWR<Agent>(
    id ? `/api/agents/${id}` : null,
    fetcher
  );

  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'live-feed' | 'people' | 'leads' | 'campaigns' | 'sends' | 'memory' | 'settings'>('live-feed');
  const [isGathering, setIsGathering] = useState(false);
  const [isRestartingScrape, setIsRestartingScrape] = useState(false);
  const [isStoppingScrape, setIsStoppingScrape] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { visibleColumns: leadVisibleColumns, saveVisibleColumns: saveLeadVisibleColumns } = useDataTableColumns({ columns: leadColumns, storageKey: LEADS_STORAGE_KEY });
  const { data: leads, mutate: mutateLeads } = useSWR<AgentLead[]>(
    id ? `/api/agents/${id}/leads` : null,
    fetcher
  );
  const { selectedIds, toggle: toggleSelection, selectAll, clearSelection, selectedCount } = useDataTableSelection({ data: leads || [], getRowId: (l) => l.id });

  const [editingCell, setEditingCell] = useState<{ rowId: number; columnId: string } | null>(null);
  const [leadCampaignModalOpen, setLeadCampaignModalOpen] = useState(false);
  const [leadsForCampaign, setLeadsForCampaign] = useState<ProspectForCampaign[]>([]);

  // Get the agent's campaign ID (created during wizard)
  const { data: agentCampaigns } = useSWR<{ id: string; name: string }[]>(
    id ? `/api/campaigns` : null,
    fetcher
  );
  // Find the campaign that matches this agent's ICP (created by wizard)
  const agentCampaignId = agentCampaigns?.find(c => c.name.includes(agent?.icp?.title || '—'))?.id || agentCampaigns?.[0]?.id;

  const handleLeadAccessEmail = (lead: AgentLead) => {
    if (!lead.prospectExternalId) {
      toast.error('This lead has no linked prospect for enrichment');
      return;
    }
    setLeadsForCampaign([{ id: lead.prospectExternalId, enrichmentStatus: 'not_enriched' as const }]);
    setLeadCampaignModalOpen(true);
  };

  const handleDeleteLead = async (lead: AgentLead) => {
    if (!id) return;
    try {
      await agentService.bulkDeleteLeads(id, [lead.id]);
      await mutateLeads();
      toast.success('Lead deleted');
    } catch {
      toast.error('Failed to delete lead');
    }
  };

  const handleBulkDeleteLeads = async () => {
    if (!id || selectedCount === 0) return;
    try {
      await agentService.bulkDeleteLeads(id, Array.from(selectedIds) as number[]);
      await mutateLeads();
      clearSelection();
      toast.success(`${selectedCount} lead(s) deleted`);
    } catch {
      toast.error('Failed to delete leads');
    }
  };

  const leadRowActions: RowAction<AgentLead>[] = [
    { label: 'Delete lead', onClick: handleDeleteLead, variant: 'destructive' },
  ];

  // Field mapping for editable cells and tab navigation
  const editableFields: { field: string; columnId: string }[] = [
    { field: 'firstName', columnId: 'firstName' },
    { field: 'lastName', columnId: 'lastName' },
    { field: 'email', columnId: 'email' },
    { field: 'title', columnId: 'role' },
    { field: 'company', columnId: 'company' },
    { field: 'companyWebsite', columnId: 'companyWebsite' },
    { field: 'location', columnId: 'location' },
  ].filter(({ columnId }) => leadVisibleColumns.includes(columnId));

  const handleLeadCellEdit = async (_leadId: number, _field: string, _value: string) => {
    // TODO: wire up API to update lead field
    await mutateLeads();
  };

  const getNextCell = (leadId: number, field: string) => {
    const fieldIndex = editableFields.findIndex(f => f.field === field);
    if (fieldIndex < editableFields.length - 1) {
      return { rowId: leadId, columnId: editableFields[fieldIndex + 1].field };
    }
    const data = leads || [];
    const rowIndex = data.findIndex(l => l.id === leadId);
    if (rowIndex < data.length - 1) {
      return { rowId: data[rowIndex + 1].id, columnId: editableFields[0].field };
    }
    return null;
  };

  const getPrevCell = (leadId: number, field: string) => {
    const fieldIndex = editableFields.findIndex(f => f.field === field);
    if (fieldIndex > 0) {
      return { rowId: leadId, columnId: editableFields[fieldIndex - 1].field };
    }
    const data = leads || [];
    const rowIndex = data.findIndex(l => l.id === leadId);
    if (rowIndex > 0) {
      return { rowId: data[rowIndex - 1].id, columnId: editableFields[editableFields.length - 1].field };
    }
    return null;
  };

  const renderLeadEditableCell = (lead: AgentLead, field: string) => {
    const isEditing = editingCell?.rowId === lead.id && editingCell?.columnId === field;
    const fieldMap: Record<string, string | null> = {
      firstName: lead.firstName, lastName: lead.lastName, email: lead.email,
      title: lead.title, company: lead.company, companyWebsite: lead.companyWebsite, location: lead.location,
    };
    const value = fieldMap[field] ?? null;

    return (
      <DataTableEditableCell
        value={value}
        rowId={lead.id}
        columnId={field}
        isEditing={isEditing}
        onStartEdit={() => setEditingCell({ rowId: lead.id, columnId: field })}
        onSave={(newValue) => { setEditingCell(null); handleLeadCellEdit(lead.id, field, newValue); }}
        onCancel={() => setEditingCell(null)}
        onTab={(newValue) => {
          handleLeadCellEdit(lead.id, field, newValue);
          const next = getNextCell(lead.id, field);
          setEditingCell(next);
        }}
        onShiftTab={(newValue) => {
          handleLeadCellEdit(lead.id, field, newValue);
          const prev = getPrevCell(lead.id, field);
          setEditingCell(prev);
        }}
      />
    );
  };

  // Build columns with custom cell renderers to match Saved Contacts
  const leadsColumnsWithCells: ColumnDefinition<AgentLead>[] = leadColumns.map(col => {
    if (col.id === 'select') {
      return {
        ...col,
        cell: (lead: AgentLead) => (
          <div className="flex items-center gap-3">
            <Checkbox checked={selectedIds.has(lead.id)} onCheckedChange={() => toggleSelection(lead.id)} />
            <div className="w-8 h-8 rounded-full bg-surface-avatar flex items-center justify-center flex-shrink-0">
              <UserIcon className="h-4 w-4 text-muted-foreground" />
            </div>
            {lead.linkedinUrl && (
              <TooltipProvider>
                <Tooltip delayDuration={300}>
                  <TooltipTrigger asChild>
                    <a href={lead.linkedinUrl} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 text-primary hover:text-primary/80 transition-colors" onClick={(e) => e.stopPropagation()}>
                      <LinkedInIcon className="h-4 w-4" />
                    </a>
                  </TooltipTrigger>
                  <TooltipContent><p className="text-xs">{lead.linkedinUrl}</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )
      };
    }
    if (col.id === 'firstName') return { ...col, cell: (l: AgentLead) => renderLeadEditableCell(l, 'firstName') };
    if (col.id === 'lastName') return { ...col, cell: (l: AgentLead) => renderLeadEditableCell(l, 'lastName') };
    if (col.id === 'email') {
      return {
        ...col,
        cell: (lead: AgentLead) => {
          if (lead.email) {
            return renderLeadEditableCell(lead, 'email');
          }
          if (lead.enrichmentAttempted) {
            return (
              <TooltipProvider>
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <div className="relative inline-block">
                      <Button
                        variant="secondary"
                        size="xs"
                        className="text-sm whitespace-nowrap shadow-xl z-10 bg-card transform-gpu"
                        onClick={() => handleLeadAccessEmail(lead)}
                      >
                        Find Email
                      </Button>
                      <div className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-danger-soft-surface rounded-full flex items-center justify-center z-20">
                        <svg className="w-2.5 h-2.5 text-danger-soft-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent><p className="text-xs">A recent attempt to find this email failed</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          }
          return (
            <Button
              variant="secondary"
              size="xs"
              className="text-sm whitespace-nowrap shadow-xl z-10 bg-card transform-gpu"
              onClick={() => handleLeadAccessEmail(lead)}
            >
              Find Email
            </Button>
          );
        }
      };
    }
    if (col.id === 'role') return { ...col, cell: (l: AgentLead) => renderLeadEditableCell(l, 'title') };
    if (col.id === 'company') return { ...col, cell: (l: AgentLead) => renderLeadEditableCell(l, 'company') };
    if (col.id === 'companyWebsite') return { ...col, cell: (l: AgentLead) => renderLeadEditableCell(l, 'companyWebsite') };
    if (col.id === 'location') return { ...col, cell: (l: AgentLead) => renderLeadEditableCell(l, 'location') };
    return col;
  });

  const { data: activityLogs, mutate: mutateActivity } = useSWR<{ id: number; message: string; type: string; createdAt: string }[]>(
    id ? `/api/agents/${id}/activity` : null,
    fetcher,
    { refreshInterval: isGathering ? 3000 : 0 }
  );

  // Poll scrape job status for live feed
  const { data: scrapeStatus, mutate: mutateScrapeStatus } = useSWR<{
    status: string;
    logs: { id: string; message: string; type: string; createdAt: string }[];
    companiesProcessed: number;
    companiesMatched: number;
    prospectsFound: number;
    startedAt: string | null;
  }>(
    id ? `/api/agents/${id}/scrape-status` : null,
    fetcher,
    { refreshInterval: agent?.scrapeJobId ? 5000 : 0 }
  );

  // Merge activity logs + scrape logs, sorted by date
  const mergedLogs = useMemo(() => {
    const agentLogs = (activityLogs || []).map(l => ({ ...l, source: 'agent' as const }));
    const scrapeLogs = (scrapeStatus?.logs || []).map(l => ({ ...l, id: `scrape-${l.id}`, source: 'scrape' as const }));
    return [...agentLogs, ...scrapeLogs].sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    ).slice(0, 50);
  }, [activityLogs, scrapeStatus?.logs]);

  const scrapeIsActive = scrapeStatus?.status === 'in_progress' || scrapeStatus?.status === 'pending';

  // Build chart data from leads by day (last 7 days)
  const chartData = useMemo(() => {
    const days: { day: string; leads: number; emails: number; replies: number; opportunities: number }[] = [];
    const now = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const dayStr = d.toLocaleDateString('en-GB', { day: 'numeric', weekday: 'short' });
      const dateKey = d.toISOString().slice(0, 10);
      const leadsOnDay = (leads || []).filter(l => l.createdAt?.slice(0, 10) === dateKey).length;
      days.push({ day: dayStr, leads: leadsOnDay, emails: 0, replies: 0, opportunities: 0 });
    }
    return days;
  }, [leads]);

  // Build proposal from agent data for the Memory tab
  const agentProfile: Phase1Proposal | null = agent ? {
    businessSummary: agent.businessSummary || '',
    offerings: agent.offerings || [],
    icp: agent.icp || { title: '', seniority: '', industry: '', companySize: '', geography: '', customFilters: [] },
    reasoning: '',
  } : null;

  const [editedProfile, setEditedProfile] = useState<Phase1Proposal | null>(null);
  const [outreachGoal, setOutreachGoal] = useState('');
  const [messagingTone, setMessagingTone] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');

  // Settings state
  const [autopilot, setAutopilot] = useState(false);
  const [autoReply, setAutoReply] = useState(false);
  const [dailyLimit, setDailyLimit] = useState(25);
  const [sendingDays, setSendingDays] = useState<string[]>(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [prioritiseNewLeads, setPrioritiseNewLeads] = useState(false);
  const [textOptOut, setTextOptOut] = useState(true);

  const toggleDay = (day: string) => {
    setSendingDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  useEffect(() => {
    if (agent) {
      if (!editedProfile && agentProfile) setEditedProfile(agentProfile);
      if (!outreachGoal && agent.outreachGoal) setOutreachGoal(agent.outreachGoal);
      if (!messagingTone && agent.messagingTone) setMessagingTone(agent.messagingTone);
      if (!websiteUrl && agent.websiteUrl) setWebsiteUrl(agent.websiteUrl);
    }
  }, [agent]);

  const handleSave = async () => {
    if (!id || !editedProfile) return;

    setIsSaving(true);
    try {
      await agentService.update(id, {
        businessSummary: editedProfile.businessSummary,
        offerings: editedProfile.offerings,
        icp: editedProfile.icp,
        outreachGoal,
        messagingTone,
        websiteUrl,
      });
      await mutate();
      toast.success('Agent saved');
    } catch {
      toast.error('Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleGatherProspects = async () => {
    if (!id || isGathering) return;
    setIsGathering(true);
    try {
      const result = await agentService.gatherProspects(id);
      await mutateLeads();
      await mutateActivity();
      await mutate();
      toast.success(`Found ${result.count} prospects`);
    } catch {
      toast.error('Failed to gather prospects');
    } finally {
      setIsGathering(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!agent || !id) return;
    const newStatus = agent.status === 'active' ? 'paused' : 'active';
    try {
      await agentService.toggleStatus(id, { status: newStatus });
      await mutate();
      toast.success(`Agent ${newStatus === 'active' ? 'activated' : 'paused'}`);
    } catch {
      toast.error('Failed to update status');
    }
  };

  const getHostname = (url: string | null) => {
    if (!url) return 'Agent';
    try {
      return new URL(url.startsWith('http') ? url : `https://${url}`).hostname.replace('www.', '');
    } catch {
      return url;
    }
  };

  if (isLoading) {
    return (
      <div className="px-6 py-10">
        <Skeleton className="h-8 w-48 mb-6" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="px-6 py-10">
        <p className="text-muted-foreground">Agent not found.</p>
        <Button variant="secondary" onClick={() => navigate('/dashboard/agents')} className="mt-4">
          Back to Agents
        </Button>
      </div>
    );
  }

  return (
    <div className="px-gutter pt-page-top pb-gutter">
      {/* Header */}
      <div className="flex items-center gap-4 mb-header-gap">
        <BackButton href="/dashboard/agents" />
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-semibold">{getHostname(agent.websiteUrl)}</h1>
            <div className="flex items-center self-center">
              <Switch checked={agent.status === 'active'} onCheckedChange={handleToggleStatus} className="translate-y-px" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-5 mb-5 flex items-center justify-between">
        <div className="w-fit">
          <Toggle
            options={[
              { value: 'live-feed', label: 'Live Feed' },
              { value: 'people', label: 'People' },
              { value: 'leads', label: 'Leads' },
              { value: 'campaigns', label: 'Campaigns' },
              { value: 'sends', label: 'Sends' },
              { value: 'memory', label: 'Memory' },
              { value: 'settings', label: 'Settings' },
            ]}
            value={activeTab}
            onChange={setActiveTab}
          />
        </div>
        {activeTab === 'leads' && (
          <div className="flex items-center gap-4">
            <Input
              placeholder="Search leads"
              prefixIcon={<MagnifyingGlassIcon className="h-5 w-5" />}
              className="max-w-sm w-70"
            />
            <Button
              variant={isGathering ? 'destructive' : 'primary'}
              onClick={handleGatherProspects}
              disabled={isGathering}
            >
              {isGathering ? 'Gathering...' : 'Procure Prospects'}
            </Button>
          </div>
        )}
      </div>

      {/* Live Feed Tab */}
      {activeTab === 'live-feed' && (
        <div className="space-y-4">
          {/* Metric cards */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-card border border-border rounded-xl p-5">
              <div className="flex items-center gap-1.5">
                <p className="text-sm text-content-strong">Leads Found</p>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger><InformationCircleIcon className="h-3.5 w-3.5 text-muted-foreground/50" /></TooltipTrigger>
                  <TooltipContent side="bottom">Prospects discovered by the agent matching your ICP</TooltipContent>
                </Tooltip>
              </div>
              <p className="text-3xl font-semibold mt-2">{agent.leadsFound}</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-5">
              <div className="flex items-center gap-1.5">
                <p className="text-sm text-content-strong">Emails Sent</p>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger><InformationCircleIcon className="h-3.5 w-3.5 text-muted-foreground/50" /></TooltipTrigger>
                  <TooltipContent side="bottom">Total personalised emails sent by the agent</TooltipContent>
                </Tooltip>
              </div>
              <p className="text-3xl font-semibold mt-2">0</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-5">
              <div className="flex items-center gap-1.5">
                <p className="text-sm text-content-strong">Replies</p>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger><InformationCircleIcon className="h-3.5 w-3.5 text-muted-foreground/50" /></TooltipTrigger>
                  <TooltipContent side="bottom">Responses received from prospects</TooltipContent>
                </Tooltip>
              </div>
              <p className="text-3xl font-semibold mt-2">0</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-5">
              <div className="flex items-center gap-1.5">
                <p className="text-sm text-content-strong">Opportunities</p>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger><InformationCircleIcon className="h-3.5 w-3.5 text-muted-foreground/50" /></TooltipTrigger>
                  <TooltipContent side="bottom">Conversations that led to a positive outcome like a meeting or demo booked</TooltipContent>
                </Tooltip>
              </div>
              <p className="text-3xl font-semibold mt-2">0</p>
            </div>
          </div>

          {/* Performance chart */}
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Agent Performance</h3>
                <p className="text-xs text-muted-foreground/60">Last 7 days</p>
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 12, fill: '#aaa' }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 'auto']} tick={{ fontSize: 12, fill: '#aaa' }} axisLine={false} tickLine={false} width={30} />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: 'var(--popover)',
                      border: '1px solid var(--line-chart)',
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      padding: '10px 14px',
                      fontSize: '13px',
                    }}
                    cursor={{ stroke: '#e5e7eb' }}
                  />
                  <Legend
                    iconType="circle"
                    iconSize={8}
                    wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
                    formatter={(value: string) => <span style={{ marginRight: '1.33rem', color: 'var(--chart-legend)' }}>{value}</span>}
                  />
                  <Line type="monotone" dataKey="leads" stroke="#3b82f6" strokeWidth={2} dot={false} name="Leads Found" />
                  <Line type="monotone" dataKey="emails" stroke="#10b981" strokeWidth={2} dot={false} name="Emails Sent" />
                  <Line type="monotone" dataKey="replies" stroke="#f59e0b" strokeWidth={2} dot={false} name="Replies" />
                  <Line type="monotone" dataKey="opportunities" stroke="#8b5cf6" strokeWidth={2} dot={false} name="Opportunities" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Activity feed */}
          {/* Scrape progress bar */}
          {agent?.icp && (
            <div className="bg-card border border-border rounded-xl p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-muted-foreground">First Phase Prospect Scraping</h3>
                {scrapeStatus?.status === 'in_progress' && (
                  <Badge variant="default">Running</Badge>
                )}
                {scrapeStatus?.status === 'pending' && (
                  <Badge variant="secondary">Starting</Badge>
                )}
                {(scrapeStatus?.status === 'completed' || scrapeStatus?.status === 'exhausted') && (
                  <Badge variant="secondary">Complete</Badge>
                )}
                {scrapeStatus?.status === 'error' && (
                  <Badge variant="destructive">Connection Lost</Badge>
                )}
              </div>
              {scrapeStatus && scrapeStatus.status !== 'none' && scrapeStatus.status !== 'error' && (
                <>
                  <div className="flex gap-6 text-sm">
                    <div><span className="text-muted-foreground">Companies:</span> {scrapeStatus.companiesProcessed}/{scrapeStatus.companiesMatched > 500 ? '500' : scrapeStatus.companiesMatched}</div>
                    <div><span className="text-muted-foreground">People found:</span> {scrapeStatus.prospectsFound}</div>
                  </div>
                  {scrapeStatus.companiesMatched > 0 && (
                    <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${Math.min(100, (scrapeStatus.companiesProcessed / Math.min(500, scrapeStatus.companiesMatched)) * 100)}%` }}
                      />
                    </div>
                  )}
                </>
              )}
              <div className="flex justify-end mt-3">
                {scrapeIsActive ? (
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={isStoppingScrape}
                    onClick={async () => {
                      setIsStoppingScrape(true);
                      try {
                        const res = await fetch(`/api/agents/${id}/stop-scrape`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' } });
                        if (res.ok) {
                          toast.success('Scrape stopped');
                          await mutate();
                          await mutateScrapeStatus();
                        } else {
                          toast.error('Failed to stop scrape');
                        }
                      } catch { toast.error('Failed to stop scrape'); }
                      finally { setIsStoppingScrape(false); }
                    }}
                  >
                    {isStoppingScrape && <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-1" />}
                    Stop Scraping
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={isRestartingScrape}
                    onClick={async () => {
                      setIsRestartingScrape(true);
                      try {
                        const res = await fetch(`/api/agents/${id}/restart-scrape`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' } });
                        if (res.ok) {
                          toast.success('Scrape started');
                          await mutate();
                          await mutateScrapeStatus();
                        } else {
                          const err = await res.json().catch(() => ({}));
                          toast.error(err.error || 'Failed to start scrape');
                        }
                      } catch { toast.error('Failed to start scrape'); }
                      finally { setIsRestartingScrape(false); }
                    }}
                  >
                    {isRestartingScrape && <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-1" />}
                    {agent?.scrapeJobId ? 'Restart Scraping' : 'Start Scraping'}
                  </Button>
                )}
              </div>
            </div>
          )}

          <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-4">Agent Activity Feed</h3>
            <div className="space-y-3">
              {mergedLogs.length > 0 ? (
                mergedLogs.map((log) => (
                  <div key={log.id} className="flex items-start gap-3 text-sm">
                    <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                      log.type === 'success' ? 'bg-success-solid' :
                      log.type === 'error' ? 'bg-danger-solid' :
                      log.type === 'prospect' ? 'bg-category-purple' :
                      log.type === 'search' ? 'bg-info-solid' :
                      log.source === 'scrape' ? 'bg-category-cyan' :
                      'bg-status-offline'
                    }`} />
                    <div>
                      <p className="text-foreground">{log.message}</p>
                      <p className="text-muted-foreground text-xs mt-0.5">
                        {new Date(log.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex items-start gap-3 text-sm">
                  <div className="w-2 h-2 rounded-full bg-success-solid mt-1.5 shrink-0" />
                  <div>
                    <p className="text-foreground">Agent created</p>
                    <p className="text-muted-foreground text-xs mt-0.5">
                      {new Date(agent.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* People Tab — scraped prospects from falcon-people, identical to Find People */}
      {activeTab === 'people' && (
        <PeopleTab
          agentId={agent.id}
          scrapeJobId={agent.scrapeJobId}
          scrapeIsActive={scrapeIsActive}
          onApproved={() => { mutateLeads(); mutate(); }}
        />
      )}

      {/* Leads Tab */}

      {activeTab === 'leads' && (
        <div className="flex flex-col" style={{ height: 'calc(100vh - 12rem)' }}>
          <div className="bg-card flex-1 min-h-0 rounded-card overflow-hidden relative flex flex-col" ref={tableContainerRef} style={{ contain: 'layout paint' }}>
            <DataTable<AgentLead>
              data={leads || []}
              columns={leadsColumnsWithCells}
              isLoading={!leads}
              resizable
              resizeConfig={{ storageKey: 'agentLeads_columnWidths' }}
              disableRowHover
              selection={{ selectedIds, onToggle: toggleSelection, onSelectAll: selectAll, onClearSelection: clearSelection }}
              rowActions={leadRowActions}
              columnVisibility={{ visibleColumns: leadVisibleColumns, onSave: saveLeadVisibleColumns, storageKey: LEADS_STORAGE_KEY }}
              emptyState={
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <UserIcon className="h-10 w-10 text-muted-foreground/30 mb-3" />
                  <p className="text-sm">No leads yet</p>
                  <p className="text-xs text-muted-foreground/60 mt-1">Click "Procure Prospects" to find leads matching your ICP</p>
                </div>
              }
            />
          </div>

          <DataTablePagination
            config={{ variant: 'animated', currentPage: 1, totalPages: 1, onPageChange: () => {} }}
            totalLabel="leads"
            total={leads?.length || 0}
          />

          <SelectionActionBar
            selectedCount={selectedCount}
            isVisible={selectedCount > 0}
            onClearSelection={clearSelection}
            itemName={{ singular: 'lead', plural: 'leads' }}
            tableContainerRef={tableContainerRef}
            selectedRowIds={Array.from(selectedIds).map(String)}
          >
            <Button variant="primary" size="xs" onClick={() => {
              const selected = (leads || [])
                .filter(l => selectedIds.has(l.id) && l.prospectExternalId)
                .map(l => ({ id: l.prospectExternalId!, enrichmentStatus: 'not_enriched' as const }));
              if (selected.length === 0) { toast.error('No selected leads have linked prospects'); return; }
              setLeadsForCampaign(selected);
              setLeadCampaignModalOpen(true);
            }}>Push to Campaign</Button>
            <Button variant="destructive" size="xs" onClick={handleBulkDeleteLeads}>Delete Selected</Button>
          </SelectionActionBar>

          <AddToCampaignModal
            open={leadCampaignModalOpen}
            onOpenChange={(open) => { setLeadCampaignModalOpen(open); }}
            prospects={leadsForCampaign}
            defaultCampaignId={agentCampaignId}
            onSuccess={async () => {
              // Sync enriched email back to agent_lead
              const idsToSync = [...leadsForCampaign];
              for (const p of idsToSync) {
                try {
                  await fetch(`/api/agents/${id}/sync-lead-email`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ prospectExternalId: p.id }),
                  });
                } catch {}
              }
              setLeadsForCampaign([]);
              mutateLeads();
            }}
          />
        </div>
      )}

      {/* Campaigns Tab */}
      {activeTab === 'campaigns' && <CampaignsTab agentId={id!} />}

      {/* Sends Tab */}
      {activeTab === 'sends' && <SendsTab agentId={id!} />}

      {/* Memory Tab */}
      {activeTab === 'memory' && editedProfile && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-muted-foreground">
              This is what the agent knows about your business. Edit anything and save.
            </p>
            <Button variant="primary" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-6">
            {/* Left column — Business details */}
            <div className="space-y-8">
              <ProposalCard title="Website">
                <Input
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  placeholder="https://yourcompany.com"
                  inlineLabel="URL"
                  className="w-full"
                />
              </ProposalCard>

              <Phase1BusinessAnalysis proposal={editedProfile} onChange={setEditedProfile} />
            </div>

            {/* Right column — Outreach strategy */}
            <div className="space-y-8">
              <ProposalCard title="Outreach Goal">
                <Textarea
                  value={outreachGoal}
                  onChange={(e) => setOutreachGoal(e.target.value)}
                  placeholder="e.g. Book discovery calls with operations directors at manufacturing plants"
                  className="min-h-[60px] resize-none overflow-hidden"
                />
              </ProposalCard>

              <ProposalCard title="Messaging Tone">
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'professional', label: 'Professional', description: 'Formal, business-appropriate' },
                    { value: 'casual', label: 'Casual', description: 'Friendly, conversational' },
                    { value: 'consultative', label: 'Consultative', description: 'Advisory, value-focused' },
                    { value: 'direct', label: 'Direct', description: 'Short, to the point' },
                  ].map((tone) => (
                    <button
                      key={tone.value}
                      type="button"
                      onClick={() => setMessagingTone(tone.value)}
                      className={`text-left p-3 rounded-md border text-sm transition-colors cursor-pointer ${
                        messagingTone === tone.value
                          ? 'border-transparent bg-surface-selected-accent-strong text-primary'
                          : 'border-border/50 hover:border-border'
                      }`}
                    >
                      <span className="font-medium">{tone.label}</span>
                      <span className={`block text-xs mt-0.5 ${messagingTone === tone.value ? 'text-primary/70' : 'text-muted-foreground'}`}>{tone.description}</span>
                    </button>
                  ))}
                </div>
              </ProposalCard>
            </div>
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="max-w-2xl space-y-6">
          {/* AI Settings */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="text-base font-semibold mb-5">AI Settings</h3>
            <div className="space-y-8">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Autopilot Mode</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Let the agent run autonomously without requiring approval at each step</p>
                </div>
                <Switch checked={autopilot} onCheckedChange={setAutopilot} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Auto-reply to responses</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Automatically respond to prospect replies with contextual messages</p>
                </div>
                <Switch checked={autoReply} onCheckedChange={setAutoReply} />
              </div>
            </div>
          </div>

          {/* Sending Settings */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="text-base font-semibold mb-5">Sending Settings</h3>
            <div className="space-y-8">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Accounts to Use</p>
                  <p className="text-xs text-muted-foreground mt-0.5">No email accounts connected yet</p>
                </div>
                <Button variant="secondary" size="sm">Connect</Button>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Daily Sending Limit</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Maximum emails per day</p>
                </div>
                <div className="flex items-center gap-3 w-48">
                  <input
                    type="range"
                    min={1}
                    max={25}
                    step={1}
                    value={dailyLimit}
                    onChange={(e) => setDailyLimit(parseInt(e.target.value))}
                    className="flex-1 h-2 appearance-none rounded-full outline-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-8 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:border-0 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-8 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-0 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:h-2"
                    style={{ background: `linear-gradient(to right, var(--slider-fill) ${((dailyLimit - 1) / 24) * 100}%, var(--slider-track) ${((dailyLimit - 1) / 24) * 100}%)` }}
                  />
                  <span className="text-sm font-medium tabular-nums w-8 text-right">{dailyLimit}</span>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-medium">Sending Days</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Days of the week the agent can send emails</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                    <button
                      key={day}
                      onClick={() => toggleDay(day)}
                      className={`w-14 h-10 text-xs font-medium rounded-full border transition-colors cursor-pointer ${
                        sendingDays.includes(day)
                          ? 'bg-info-surface-strong text-info-foreground border-info-border hover:bg-info-surface-hover'
                          : 'bg-input-bg text-secondary-foreground border-border hover:bg-input-bg shadow-lg'
                      }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Sending Window</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Time range when emails can be sent</p>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={startTime} onValueChange={setStartTime}>
                    <SelectTrigger className="w-32 h-10 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }, (_, h) => {
                        const time = `${String(h).padStart(2, '0')}:00`;
                        const label = h === 0 ? '12:00 AM' : h < 12 ? `${h}:00 AM` : h === 12 ? '12:00 PM' : `${h - 12}:00 PM`;
                        return <SelectItem key={time} value={time}>{label}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                  <span className="text-muted-foreground text-xs">to</span>
                  <Select value={endTime} onValueChange={setEndTime}>
                    <SelectTrigger className="w-32 h-10 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }, (_, h) => {
                        const time = `${String(h).padStart(2, '0')}:00`;
                        const label = h === 0 ? '12:00 AM' : h < 12 ? `${h}:00 AM` : h === 12 ? '12:00 PM' : `${h - 12}:00 PM`;
                        return <SelectItem key={time} value={time}>{label}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          {/* Deliverability */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="text-base font-semibold mb-5">Deliverability</h3>
            <div className="space-y-8">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Prioritise New Leads</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Send to new prospects first before follow-ups</p>
                </div>
                <Switch checked={prioritiseNewLeads} onCheckedChange={setPrioritiseNewLeads} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Text-based Opt-Out</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Append a plain-text unsubscribe line to every email</p>
                </div>
                <Switch checked={textOptOut} onCheckedChange={setTextOptOut} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
